import React from "react";
import { ShieldCheck, ShieldAlert, ShieldX, Hourglass, Clock, TrendingUp } from "lucide-react";
import { useTranslation } from "@/lib/i18n";

export const RISK_TOKENS = {
  Low: { fg: "#3FAE89", bg: "rgba(63,174,137,0.12)", border: "rgba(63,174,137,0.32)", icon: ShieldCheck },
  Medium: { fg: "#E0B341", bg: "rgba(224,179,65,0.12)", border: "rgba(224,179,65,0.34)", icon: ShieldAlert },
  High: { fg: "#E5484D", bg: "rgba(229,72,77,0.12)", border: "rgba(229,72,77,0.34)", icon: ShieldX },
};

export const HORIZON_TOKENS = {
  "Short-term": { icon: Clock },
  "Mid-term": { icon: Hourglass },
  "Long-term": { icon: TrendingUp },
};

export function DecisionStrip({ valuation, compact = false, className = "" }) {
  const { t } = useTranslation();
  if (!valuation) return null;
  // Feb 2026 — Valuation-safety unavailable state: never fabricate
  // a "Medium risk / Mid-term horizon" default when the backend
  // explicitly returned an unavailable analysis.
  const isUnavailable =
    valuation.status === "Unavailable" || valuation.signal === "Unavailable";
  // Phase 6.AH null-truthfulness: when the backend couldn't compute an
  // upside (e.g. fair_value unavailable due to plan_restricted endpoints)
  // we must NOT silently coerce it to 0% — that misleads users into
  // thinking the stock is fairly priced when in reality we have no
  // valuation. Render "—" instead and skip the +/- sign affordance.
  const rawUpside = valuation.upside_pct;
  const upsideKnown = rawUpside !== null && rawUpside !== undefined
    && Number.isFinite(Number(rawUpside));
  const upside = upsideKnown ? Number(rawUpside) : null;
  const upsideAbs = upsideKnown ? Math.abs(upside).toFixed(1) : null;

  // Upside-chip color follows the SIGNAL so a value-trap "Undervalued by X%"
  // doesn't read as a green opportunity. Falls back to status when signal is
  // absent (legacy payload).
  const sig = valuation.signal;
  // When upside is unknown, neutralize the chip tone (don't lean green/red
  // on a fake-zero baseline).
  const upsideTone = !upsideKnown
    ? { fg: "#A8AEB7", bg: "rgba(168,174,183,0.10)", border: "rgba(168,174,183,0.28)" }
    : sig === "Opportunity"
    ? { fg: "#3FAE89", bg: "rgba(63,174,137,0.14)", border: "rgba(63,174,137,0.34)" }
    : sig === "Risk"
      ? { fg: "#E5484D", bg: "rgba(229,72,77,0.14)", border: "rgba(229,72,77,0.34)" }
      : sig === "Neutral"
        ? { fg: "#E0B341", bg: "rgba(224,179,65,0.14)", border: "rgba(224,179,65,0.34)" }
        : valuation.status === "Undervalued"
          ? { fg: "#3FAE89", bg: "rgba(63,174,137,0.14)", border: "rgba(63,174,137,0.34)" }
          : valuation.status === "Overvalued"
            ? { fg: "#E5484D", bg: "rgba(229,72,77,0.14)", border: "rgba(229,72,77,0.34)" }
            : { fg: "#E0B341", bg: "rgba(224,179,65,0.14)", border: "rgba(224,179,65,0.34)" };
  const neutralTone = { fg: "#A8AEB7", bg: "rgba(168,174,183,0.10)", border: "rgba(168,174,183,0.28)" };
  // Risk level is a unit-safe assessment (derived from beta,
  // profitability, leverage — not currency-dependent).  Preserve
  // the backend-computed value even when the valuation itself is
  // unavailable, so users still see the safety context.
  const risk = (valuation.risk_level && RISK_TOKENS[valuation.risk_level]) || null;
  // Time horizon depends on the valuation upside, so it is
  // suppressed when the valuation is unavailable.
  const horizonTok = (!isUnavailable && valuation.time_horizon && HORIZON_TOKENS[valuation.time_horizon]) || null;
  const RiskIcon = (risk || RISK_TOKENS.Medium).icon;
  const HorizonIcon = (horizonTok || HORIZON_TOKENS["Mid-term"]).icon;

  const sizing = compact
    ? "text-[10px] px-2 py-1 gap-1.5"
    : "text-[11px] px-2.5 py-1.5 gap-1.5";

  return (
    <div className={`grid grid-cols-3 gap-1.5 ${className}`} data-testid="decision-strip">
      <Chip
        testId="decision-upside"
        sizing={sizing}
        tone={upsideTone}
        label={t("analyze.chip_upside")}
        showLabel={!compact}
        value={!upsideKnown ? "\u2014" : upside >= 0 ? `+${upsideAbs}%` : `-${upsideAbs}%`}
        icon={<TrendingUp className="w-3 h-3" />}
      />
      <Chip
        testId="decision-risk"
        sizing={sizing}
        tone={risk || neutralTone}
        label={t("analyze.chip_risk")}
        showLabel={!compact}
        value={risk ? (t("analyze.status." + valuation.risk_level.toLowerCase()) || valuation.risk_level) : "\u2014"}
        icon={<RiskIcon className="w-3 h-3" />}
      />
      <Chip
        testId="decision-horizon"
        sizing={sizing}
        tone={null}
        label={t("analyze.chip_horizon")}
        showLabel={!compact}
        value={horizonTok ? t("analyze.horizon." + valuation.time_horizon) : "\u2014"}
        icon={<HorizonIcon className="w-3 h-3" />}
      />
    </div>
  );
}

function Chip({ testId, sizing, tone, label, value, icon, showLabel }) {
  // Phase 7.UI.8 — chip label/value now use `text-muted-foreground` and
  // `text-foreground` Tailwind utilities (theme-aware) instead of the
  // hardcoded white/light-grey colors, so the chip reads cleanly in
  // BOTH dark and light themes.
  const baseStyle = tone
    ? { color: tone.fg, background: tone.bg, border: `1px solid ${tone.border}` }
    : undefined;
  return (
    <div
      className={`rounded-md inline-flex items-center justify-center ${sizing} ${
        tone ? "" : "bg-muted/10 border border-border/30"
      }`}
      style={baseStyle}
      data-testid={testId}
      title={`${label}: ${value}`}
    >
      <span className="inline-flex items-center gap-1 shrink-0 text-muted-foreground/75">
        {icon}
        {showLabel ? (
          <span className="uppercase tracking-wider text-[10px]">{label}</span>
        ) : null}
      </span>
      <span
        className={`font-mono tabular text-center ml-1.5 whitespace-nowrap ${tone ? "" : "text-foreground"}`}
        style={tone ? { color: tone.fg } : undefined}
      >
        {value}
      </span>
    </div>
  );
}
